import { Templates } from '@hellohair/types';
import { Box } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddRowButton, CollapsibleRow, FieldGroup, RowSummary, StepField } from './fields';
import { appendTo, removeAt, replaceAt } from './stepData';

/**
 * The modal list shared by every step type that can link modals from its
 * consent text. Each entry is a foldout - collapsed it shows only id and
 * title, so a step with several long modal texts stays scannable.
 */
export const ModalList: FC<{
  modals: Templates.RichTextModal[];
  onChange: (modals: Templates.RichTextModal[]) => void;
}> = ({ modals, onChange }) => {
  const { t } = useTranslation();
  // Only one modal is unfolded at a time; a freshly added one opens itself.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const remove = (i: number) => {
    onChange(removeAt(modals, i));
    setOpenIndex(null);
  };

  return (
    <FieldGroup title={t('editor.section.modals')} hint={t('editor.hint.modals')}>
      {modals.map((modal, i: number) => (
        <CollapsibleRow
          key={i}
          expanded={openIndex === i}
          onToggle={(expanded) => setOpenIndex(expanded ? i : null)}
          removeLabel={t('editor.action.removeModal')}
          onRemove={() => remove(i)}
          summary={
            <RowSummary
              badge={modal.id ? t('editor.modalId', { id: modal.id }) : t('editor.hint.untitledModal')}
              muted={!modal.id}
              title={modal.title}
            />
          }
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: '140px' }}>
              <StepField
                label={t('editor.field.id')}
                value={modal.id}
                onChange={(value) => onChange(replaceAt(modals, i, { id: value }))}
              />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <StepField
                label={t('editor.field.title')}
                value={modal.title}
                onChange={(value) => onChange(replaceAt(modals, i, { title: value }))}
              />
            </Box>
          </Box>
          <StepField
            label={t('editor.field.content')}
            value={modal.content}
            onChange={(value) => onChange(replaceAt(modals, i, { content: value }))}
            multiline
          />
        </CollapsibleRow>
      ))}
      <AddRowButton
        label={t('editor.add.modal')}
        onClick={() => {
          onChange(appendTo(modals, { id: '', title: '', content: '' }));
          setOpenIndex(modals.length);
        }}
      />
    </FieldGroup>
  );
};
